/**
 * Install + registration + push opt-in attribution.
 *
 * Bridges the ad lander at papacitoapp.com/install (which stores tracking
 * params under localStorage["papacito_tracking"]) with the SPA's pixel +
 * Keitaro flow. Three events fire, each deduped per device:
 *
 *   1. Install — when the user first opens the PWA in standalone mode.
 *      fbq('track', 'Subscribe') + CAPI Subscribe + Keitaro status=install
 *
 *   2. Registration — when onboarding completes (alongside existing
 *      pixelRegistration()).
 *      CAPI CompleteRegistration + Keitaro status=registered
 *
 *   3. Push opt-in — when web push subscription is saved successfully.
 *      fbq('trackCustom', 'PushOptIn') + CAPI PushOptIn + Keitaro
 *      status=push_optin
 */

import { pixelTrack, pixelCustom } from './pixel';

const TRACKING_KEY         = 'papacito_tracking';
const INSTALL_FIRED_KEY    = 'papacito_install_fired';
const REGISTER_FIRED_KEY   = 'papacito_register_fired';
const PUSH_OPTIN_FIRED_KEY = 'papacito_push_optin_fired';

export interface TrackingParams {
  subid?: string;
  fbclid?: string;
  ttclid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  [key: string]: string | undefined;
}

/**
 * Reads tracking params written by /install. Tries localStorage first
 * (fast, same-context), then falls back to the cookie set by install.html.
 *
 * Why the cookie fallback matters: on iOS versions before 16.4, when a user
 * adds the site to their home screen and opens it from the icon, the PWA
 * runs in a separate webview context with its OWN localStorage — Safari's
 * tracking writes are invisible to the PWA. Cookies scoped to the host stay
 * accessible to both contexts. Without this fallback, ad-attributed installs
 * from older iOS users register but never fire the install/Keitaro postback
 * because subid is gone by the time the SPA boots in standalone mode.
 *
 * On successful cookie read we repopulate localStorage so subsequent reads
 * are fast and a single conversion fires per device (the dedupe flags are
 * also stored in localStorage).
 */
function readTrackingCookie(): TrackingParams | null {
  if (typeof document === 'undefined') return null;
  try {
    const cookies = document.cookie ? document.cookie.split(';') : [];
    for (const c of cookies) {
      const trimmed = c.trim();
      if (trimmed.startsWith(TRACKING_KEY + '=')) {
        const value = trimmed.slice(TRACKING_KEY.length + 1);
        if (!value) return null;
        return JSON.parse(decodeURIComponent(value)) as TrackingParams;
      }
    }
  } catch {
    // fallthrough
  }
  return null;
}

export function getInstallTracking(): TrackingParams {
  try {
    const raw = localStorage.getItem(TRACKING_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as TrackingParams;
      if (parsed && (parsed.subid || parsed.fbclid)) return parsed;
    }
  } catch {
    // localStorage may throw in some contexts (private mode, etc.); fall through
  }

  const fromCookie = readTrackingCookie();
  if (fromCookie && (fromCookie.subid || fromCookie.fbclid)) {
    // Repopulate localStorage so dedupe flags + future reads work normally.
    try { localStorage.setItem(TRACKING_KEY, JSON.stringify(fromCookie)); } catch {}
    return fromCookie;
  }

  return {};
}

/** True iff the SPA is running as an installed PWA (home-screen launch). */
function isStandaloneMode(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    (window.navigator as { standalone?: boolean }).standalone === true ||
    document.referrer.includes('android-app://')
  );
}

function detectPlatform(): 'ios' | 'android' | 'desktop' {
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}

/**
 * Generates a per-event UUID used to dedupe client-side fbq calls against
 * server-side CAPI events. Both must include the same event_id for FB to
 * collapse them into a single conversion in Events Manager.
 */
function generateEventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback: timestamp + random for older browsers (vanishingly rare in
  // 2026 but not worth crashing the conversion flow over).
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Sends a conversion event to /api/tracking/capi. The server fires both
 * Facebook CAPI and (when subid is present) the Keitaro postback.
 *
 * Uses fetch with keepalive so the request survives any subsequent route
 * change. Failures are logged but never thrown — pixel firing must never
 * break the user flow.
 */
async function fireServerConversion(
  eventName: string,
  eventId: string,
  extra: Record<string, unknown> = {}
): Promise<void> {
  const tracking = getInstallTracking();
  const payload = {
    event_name: eventName,
    event_id:   eventId,
    event_time: Math.floor(Date.now() / 1000),
    fbclid:     tracking.fbclid || '',
    subid:      tracking.subid || '',
    user_agent: navigator.userAgent,
    source_url: window.location.href,
    platform:   detectPlatform(),
    ...extra,
  };
  try {
    await fetch('/api/tracking/capi', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (err) {
    console.warn('[InstallAttr]', eventName, 'CAPI failed:', err);
  }
}

/**
 * Fires the install conversion if (a) the SPA is running in standalone
 * mode, (b) we have ad-attribution params from /install, and (c) we
 * haven't already fired for this device.
 *
 * Safe to call on every app boot — the dedupe flag makes repeat calls a
 * no-op.
 */
export function trackInstallIfFirstStandaloneRun(): void {
  if (typeof window === 'undefined') return;
  if (!isStandaloneMode()) return;
  if (localStorage.getItem(INSTALL_FIRED_KEY)) return;

  const tracking = getInstallTracking();
  // Only fire for ad-attributed installs. Organic installs (no subid +
  // no fbclid) shouldn't pollute the FB pixel's optimization signal.
  if (!tracking.subid && !tracking.fbclid) return;

  const eventId = generateEventId();
  pixelTrack('Subscribe', {
    content_name: 'PWA Install',
    currency:     'USD',
    value:        0,
  }, eventId);

  void fireServerConversion('Subscribe', eventId, { content_name: 'PWA Install' });

  localStorage.setItem(INSTALL_FIRED_KEY, '1');
  console.log('[InstallAttr] install conversion fired', tracking);
}

/**
 * Fires the registration conversion. Called from the onboarding-complete
 * handler alongside the existing pixelRegistration() so both the FB pixel
 * (client-side) and the CAPI mirror + Keitaro postback (server-side) all
 * land at the same moment.
 *
 * Email is hashed server-side for FB advanced matching.
 */
export function trackRegistrationConversion(email?: string): void {
  if (typeof window === 'undefined') return;
  if (localStorage.getItem(REGISTER_FIRED_KEY)) return;

  // pixelRegistration() (called separately at registration completion)
  // fires 'CompleteRegistration' on the client side. We share an event_id
  // here so FB dedupes the client + server pair into a single conversion.
  const eventId = generateEventId();
  pixelTrack('CompleteRegistration', {
    content_name: 'Papacito Signup',
    currency:     'USD',
    value:        0,
    status:       true,
  }, eventId);

  void fireServerConversion('CompleteRegistration', eventId, {
    content_name: 'Papacito Registration',
    email:        email || '',
  });

  localStorage.setItem(REGISTER_FIRED_KEY, '1');
  console.log('[InstallAttr] registration conversion fired');
}

/**
 * Fires the push opt-in conversion when the user grants notification
 * permission AND the server confirms the push subscription was saved.
 * Called from pushPermissions.ts on the success path of subscribeToPush().
 *
 * Only fires for ad-attributed users (subid or fbclid in localStorage) —
 * organic opt-ins don't count toward FB pixel optimization. This is the
 * primary success metric for the install-page A/B test, so attribution
 * accuracy matters.
 *
 * Push opt-in uses a custom event ('PushOptIn') rather than a standard
 * event so it doesn't pollute the Subscribe / CompleteRegistration data.
 * To optimize FB ad delivery on push opt-ins, set up a Custom Conversion
 * in Events Manager that maps to this event.
 */
export function trackPushOptInConversion(): void {
  if (typeof window === 'undefined') return;
  if (localStorage.getItem(PUSH_OPTIN_FIRED_KEY)) return;

  const tracking = getInstallTracking();
  if (!tracking.subid && !tracking.fbclid) return;

  const eventId = generateEventId();
  pixelCustom('PushOptIn', {
    content_name: 'Web Push Opt-In',
  }, eventId);

  void fireServerConversion('PushOptIn', eventId, { content_name: 'Web Push Opt-In' });

  localStorage.setItem(PUSH_OPTIN_FIRED_KEY, '1');
  console.log('[InstallAttr] push opt-in conversion fired');
}
